import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Globe, ChevronLeft, Play, CheckCircle, Circle, 
  Clock, BookOpen, Lock, Menu, X, FileText
} from 'lucide-react';

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [completedChapters, setCompletedChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        // Fetch course details
        const courseRes = await axios.get(`${API}/courses/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCourse(courseRes.data);

        // Fetch chapters
        const chaptersRes = await axios.get(`${API}/courses/${courseId}/chapters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChapters(chaptersRes.data);
        if (chaptersRes.data.length > 0) {
          setCurrentChapter(chaptersRes.data[0]);
        }

        // Fetch progress
        const progressRes = await axios.get(`${API}/user/progress/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompletedChapters(progressRes.data?.completed_chapters || []);
      } catch (error) {
        console.error('Failed to fetch course data:', error);
        if (error.response?.status === 403) {
          toast.error("You haven't purchased this course");
          navigate('/dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, token, navigate]);

  const handleChapterSelect = (chapter) => {
    setCurrentChapter(chapter);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!currentChapter) return;
    
    try {
      await axios.post(
        `${API}/user/progress/${courseId}/${currentChapter.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!completedChapters.includes(currentChapter.id)) {
        setCompletedChapters([...completedChapters, currentChapter.id]);
        toast.success('Chapter marked as complete!');
      }

      // Auto-advance to next chapter
      const currentIndex = chapters.findIndex(c => c.id === currentChapter.id);
      if (currentIndex < chapters.length - 1) {
        setCurrentChapter(chapters[currentIndex + 1]);
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const progressPercentage = chapters.length > 0 
    ? (completedChapters.length / chapters.length) * 100 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="course-page">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-80 bg-slate-800 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-700">
            <Link to="/dashboard" className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-4">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Back to Dashboard</span>
            </Link>
            <h2 className="text-lg font-bold text-white line-clamp-2">{course?.title}</h2>
            <div className="mt-3">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Progress</span>
                <span className="text-emerald-400">{Math.round(progressPercentage)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2 bg-slate-700" />
            </div>
          </div>

          {/* Chapters List */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-2">
              {chapters.map((chapter, idx) => {
                const isCompleted = completedChapters.includes(chapter.id);
                const isCurrent = currentChapter?.id === chapter.id;
                
                return (
                  <button
                    key={chapter.id}
                    onClick={() => handleChapterSelect(chapter)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isCurrent 
                        ? 'bg-emerald-600/20 border border-emerald-500/50' 
                        : 'hover:bg-slate-700/50'
                    }`}
                    data-testid={`chapter-${chapter.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isCompleted 
                          ? 'bg-emerald-500 text-white' 
                          : isCurrent 
                            ? 'bg-emerald-500/30 text-emerald-400' 
                            : 'bg-slate-700 text-slate-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <span className="text-xs font-medium">{idx + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium line-clamp-2 ${
                          isCurrent ? 'text-white' : isCompleted ? 'text-slate-300' : 'text-slate-400'
                        }`}>
                          {chapter.title}
                        </p>
                        {chapter.duration_minutes && (
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {chapter.duration_minutes} min
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 p-4 sticky top-0 z-20">
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full gradient-emerald flex items-center justify-center">
                <Globe className="w-4 h-4 text-amber-500" />
              </div>
              <span className="font-bold text-white hidden sm:block">Innovator Founder Visas</span>
            </div>

            <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              {completedChapters.length}/{chapters.length} Complete
            </Badge>
          </div>
        </div>

        {/* Video Player Area */}
        <div className="max-w-5xl mx-auto p-4 lg:p-8">
          {currentChapter ? (
            <>
              {/* Video Player */}
              <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden mb-6 relative">
                {currentChapter.video_url ? (
                  <iframe
                    src={currentChapter.video_url}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <Play className="w-16 h-16 mb-4" />
                    <p className="text-lg">Video coming soon</p>
                    <p className="text-sm text-slate-500 mt-2">Check back later for this lesson</p>
                  </div>
                )}
              </div>

              {/* Chapter Info */}
              <div className="bg-slate-800 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                  <div>
                    <Badge className="bg-slate-700 text-slate-300 mb-2">
                      Chapter {chapters.findIndex(c => c.id === currentChapter.id) + 1}
                    </Badge>
                    <h1 className="text-2xl font-bold text-white">{currentChapter.title}</h1>
                  </div>
                  <Button 
                    onClick={handleMarkComplete}
                    className={`${
                      completedChapters.includes(currentChapter.id) 
                        ? 'bg-emerald-600 hover:bg-emerald-700' 
                        : 'btn-primary'
                    }`}
                    data-testid="mark-complete-btn"
                  >
                    {completedChapters.includes(currentChapter.id) ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Completed
                      </>
                    ) : (
                      'Mark as Complete'
                    )}
                  </Button>
                </div>

                {currentChapter.description && (
                  <p className="text-slate-300 leading-relaxed">{currentChapter.description}</p>
                )}

                {currentChapter.duration_minutes && (
                  <div className="flex items-center gap-2 text-slate-400 mt-4">
                    <Clock className="w-4 h-4" />
                    <span>{currentChapter.duration_minutes} minutes</span>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    const idx = chapters.findIndex(c => c.id === currentChapter.id);
                    if (idx > 0) setCurrentChapter(chapters[idx - 1]);
                  }}
                  disabled={chapters.findIndex(c => c.id === currentChapter.id) === 0}
                  className="text-slate-400 border-slate-600 hover:bg-slate-800"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={() => {
                    const idx = chapters.findIndex(c => c.id === currentChapter.id);
                    if (idx < chapters.length - 1) setCurrentChapter(chapters[idx + 1]);
                  }}
                  disabled={chapters.findIndex(c => c.id === currentChapter.id) === chapters.length - 1}
                  className="btn-primary"
                >
                  Next
                  <ChevronLeft className="w-4 h-4 ml-2 rotate-180" />
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Select a chapter to begin</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoursePage;
